import * as React from "react";

import { Alert, Badge, Button, Checkbox, Spinner } from "Components/ui";
import { RefreshCw, ShieldCheck } from "Components/ui/icons";
import { getApiErrorMessage } from "api/apiError";
import { TeleopdRolesResult } from "api/teleopdLink";
import { cn } from "utils/cn";
import { formatDateTime, formatRelative } from "utils/format";
import { AudienceRole } from "../rules";

interface AudiencePickerProps {
  linked: boolean;
  roles: TeleopdRolesResult | undefined;
  loading: boolean;
  fetching: boolean;
  error: unknown;
  selected: AudienceRole[];
  /** Offered while nothing is chosen (TeleOpd's full-access roles); applied only on click. */
  suggested: AudienceRole[];
  disabled: boolean;
  onChange: (next: AudienceRole[]) => void;
  onRefresh: () => void;
}

/** "Who sees warnings": TeleOpd roles (live or from the last sync) as a checklist. */
const AudiencePicker: React.FC<AudiencePickerProps> = ({
  linked,
  roles,
  loading,
  fetching,
  error,
  selected,
  suggested,
  disabled,
  onChange,
  onRefresh,
}) => {
  const selectedIds = selected.map((role) => role.id);
  const available = roles ? roles.roles : [];
  const missing = selected.filter((role) => !available.some((entry) => entry.id === role.id));

  const toggle = (role: AudienceRole) => {
    if (disabled) {
      return;
    }
    onChange(
      selectedIds.includes(role.id)
        ? selected.filter((entry) => entry.id !== role.id)
        : [...selected, role]
    );
  };

  if (!linked) {
    return (
      <div className="space-y-2">
        <Alert tone="neutral">
          Connect TeleOpd (Connection tab) to pick roles from its live list.
        </Alert>
        {selected.length > 0 && (
          <p className="text-sm text-muted-foreground">
            Saved: {selected.map((role) => role.name).join(", ")}
          </p>
        )}
      </div>
    );
  }

  let list: React.ReactNode;
  if (loading) {
    list = (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Spinner />
        Loading TeleOpd roles…
      </div>
    );
  } else if (error) {
    list = (
      <Alert tone="danger" title="Roles didn't load">
        {getApiErrorMessage(error)}
      </Alert>
    );
  } else if (available.length === 0 && missing.length === 0) {
    list = <p className="text-sm text-muted-foreground">TeleOpd reported no roles yet.</p>;
  } else {
    list = (
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {available.map((role) => {
          const checked = selectedIds.includes(role.id);
          return (
            <li
              key={role.id}
              className={cn(
                "rounded-control border px-3 py-2 transition-colors duration-fast ease-standard",
                checked ? "border-primary/40 bg-primary-subtle" : "border-border bg-surface",
                !disabled && !checked && "hover:border-primary/30"
              )}
            >
              <Checkbox
                checked={checked}
                disabled={disabled}
                onCheckedChange={() => toggle({ id: role.id, name: role.name })}
                className="min-w-0"
                label={
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate">{role.name}</span>
                    {role.fullAccess && (
                      <Badge tone="neutral">
                        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                        Full access
                      </Badge>
                    )}
                  </span>
                }
              />
            </li>
          );
        })}
        {missing.map((role) => (
          <li
            key={role.id}
            className="rounded-control border border-warning/30 bg-warning-subtle px-3 py-2"
          >
            <Checkbox
              checked
              disabled={disabled}
              onCheckedChange={() => toggle(role)}
              className="min-w-0"
              label={
                <span className="block truncate">
                  {role.name}{" "}
                  <span className="text-xs font-normal text-warning-text">
                    (no longer in TeleOpd)
                  </span>
                </span>
              }
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {roles &&
            (roles.source === "live" ? (
              <Badge tone="success" dot>
                Live from TeleOpd
              </Badge>
            ) : (
              <Badge
                tone="neutral"
                title={roles.fetchedAt ? formatDateTime(roles.fetchedAt) : undefined}
              >
                From last sync{roles.fetchedAt ? ` · ${formatRelative(roles.fetchedAt)}` : ""}
              </Badge>
            ))}
          {roles && roles.liveError && (
            <span className="text-muted-foreground">
              Live list unavailable: {roles.liveError.message}
            </span>
          )}
        </div>
        <Button
          variant="secondary"
          size="xs"
          onClick={onRefresh}
          loading={fetching}
          leftIcon={<RefreshCw aria-hidden="true" />}
        >
          Refresh
        </Button>
      </div>

      {list}

      {suggested.length > 0 && selected.length === 0 && !disabled && (
        <Alert
          tone="info"
          action={
            <Button variant="secondary" size="xs" onClick={() => onChange(suggested)}>
              Use these roles
            </Button>
          }
        >
          Suggested: TeleOpd&apos;s {suggested.length} full-access{" "}
          {suggested.length === 1 ? "role" : "roles"}. Nothing changes until you pick roles and
          save.
        </Alert>
      )}
      {selected.length === 0 && !loading && (
        <p className="text-xs text-warning-text">
          Nobody is chosen: during the warning period no one sees the amount or the Pay now button.
        </p>
      )}
    </div>
  );
};

export default AudiencePicker;
